import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, updateData, getcountries, getpaymentType, getseries, getsubdomains  } from '../../../services/admin/ParticipantForm/approvedOnsiteParticipantFormServices';
import { Modal, Button, Table } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import Swal from 'sweetalert2';

const ApprovedOnsiteParticipantForm = () => {
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState({
    id: '',
    billing_address: '',
    billing_countries: '',
    billing_email: '',
    billing_full_name: '',
    billing_phone_no: '',
    createdAt: '',
    currency_type: '',
    no_of_ticket: '',
    occupancy_type: '',
    payment_id: '',
    payment_type: '',
    participant_type: '',
    onsite_status: '',
    refund_status: '',
    reg_amount: '',
    reg_category: '',
    reg_type: '',
    series: '',
    sub_domain: '',
    subject: '',
    updatedAt: '',
  });
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [seriesFilter, setSeriesFilter]       = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [dropdownData, setDropdownData] = useState({
    countries: [],
    paymentType: [],
    series: [],       
    subdomains: [], 
  });
  

  // Fetch approved data list (onsite_status: 1)
  const fetchData = useCallback(async () => {
  try {
    const [data1, data2] = await Promise.all([
      getData({ onsite_status: 1 }),
      getData({ onsite_status: 2 }),
    ]);
    setDataList([...data1, ...data2]);
  } catch (error) {
    console.error('Failed to fetch data:', error);
  }
}, []);

  // Fetch all dropdown data
  const fetchDropdownData = useCallback(async () => {
  try {
    const [country, payment, subdomains, series] = await Promise.all([
      getcountries(),
      getpaymentType(),
      getsubdomains(),
      getseries(),
    ]);

    const uniqueSeries = [...new Set(series.map(item => item.series))].map(s => ({ series: s }));

    setDropdownData({
      countries: country,
      paymentType: payment,
      subdomains: subdomains,
      series: uniqueSeries,
    });
  } catch (error) {
    console.error('Error fetching dropdown data:', error);
  }
}, []);

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);
      await fetchData();
      await fetchDropdownData();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setLoading(false);
    };
    fetchDataAsync();
  }, [fetchData, fetchDropdownData]);

  const openModal = (data) => {
    setForm(data);
    setShowModal(true);
    };

    const handleClose = async () => {
    try {
        const updatedForm = { ...form, onsite_status: 2 };
        await updateData(updatedForm);
        await fetchData();
        setShowModal(false);

        Swal.fire({
        icon: 'success',
        title: 'Paid!',
        text: 'Onsite participant marked as paid.',
        timer: 2000,
        showConfirmButton: false,
        });
    } catch (error) {
        console.error('Error updating paid status:', error);
        Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Something went wrong. Please try again.',
        });
    }
    };

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange = (page) => setCurrentPage(page);

  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'INR': return '₹';
      case 'GBP': return '£';
      case 'RUB': return '₽';
      case 'THB': return '฿';
      case 'MYR': return 'RM'; 
      default: return currencyCode || '';
    }
  };

  const getCountryName = (code) => {
    const country = dropdownData.countries.find((c) => c.code === code);
    return country ? country.name : code;
  };

  const getPaymentTypeName = (code) => {
    const payment = dropdownData.paymentType.find((c) => c.code === code);
    return payment ? payment.type : code;
  };

  const filteredData = useMemo(() => {
    return dataList
      .filter((data) => {
        const matchesText =
          data.billing_full_name.toLowerCase().includes(searchText.toLowerCase()) ||
          data.sub_domain.toLowerCase().includes(searchText.toLowerCase());

        const matchSeries    = !seriesFilter    || data.series     === seriesFilter;
        const matchSubDomain = !subDomainFilter || data.sub_domain === subDomainFilter;

        return matchesText && matchSeries && matchSubDomain;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [dataList, searchText, seriesFilter, subDomainFilter]);

  const currencyTotals = useMemo(() => {
  const totals = { USD: 0, EUR: 0, MYR: 0 };
  filteredData
    .filter((row) => row.onsite_status === 2)   // ← only paid
    .forEach((row) => {
      const currency = row.currency_type?.toUpperCase();
      const amount = parseFloat(row.reg_amount) || 0;
      if (totals.hasOwnProperty(currency)) {
        totals[currency] += amount;
      }
    });
  return totals;
}, [filteredData]);

  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      sortable: false,
      width: '10%',
    },
    {
      name: 'Series',
      selector: (row) => {
        const date = new Date(row.series);
        const year = date.getFullYear();
        const month = date.getMonth();
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[month]} ${year}`;
      },
      sortable: true,
      wrap: true,
      width: '10%',
    },
    {
      name: 'Sub Domain',
      selector: (row) => row.sub_domain,
      sortable: true,
      wrap: true,
      width: '10%',
    },
    {
      name: 'Name',
      selector: (row) => row.billing_full_name,
      sortable: true,
      wrap: true,
      width: '15%',
    },
    {
      name: 'Country',
      selector: (row) => getCountryName(row.billing_countries),
      sortable: true,
      wrap: true,
      width: '15%',
    },
    {
      name: 'Reg Amount',
      selector: (row) => {
        const currencySymbol = getCurrencySymbol(row.currency_type);
        return <b className="fs-4">{currencySymbol} {row.reg_amount}</b>;
      },
      sortable: true,
      wrap: true,
      width: '10%',
    },
    {
    name: 'Status',
    selector: (row) => row.onsite_status,
    cell: (row) => {
        switch (row.onsite_status) {
        case 1:
            return <span className="badge bg-warning w-100">Waiting</span>;
        case 2:
            return <span className="badge bg-success w-100 py-1">Paid</span>;
        default:
            return <span className="badge bg-warning text-dark">Pending</span>;
        }
    },
    sortable: true,
    wrap: true,
    width: '10%',
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <Button variant="info" size="sm" onClick={() => openModal(row)}>
            <i className="align-middle far fa-fw fa-eye"></i> View
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        <div className="row m-3 gap-3">
          <div className="col-md-2">
            <span className='fs-5 text-dark'>Series</span>
            <select className="form-select" value={seriesFilter} onChange={(e) => setSeriesFilter(e.target.value)}>
              <option value="">All Series</option>
              {dropdownData.series
                .sort((a, b) => b.series.localeCompare(a.series))
                .map((s) => (
                  <option key={s.series} value={s.series}>{s.series}</option>
                ))}
            </select>
          </div>
          <div className="col-md-2">
            <span className='fs-5 text-dark'>Sub Domain</span>
            <select className="form-select" value={subDomainFilter} onChange={(e) => setSubDomainFilter(e.target.value)}>
              <option value="">All Subdomains</option>
              {dropdownData.subdomains
                .sort((a, b) => a.sub_domain.localeCompare(b.sub_domain))
                .map((s) => (
                  <option key={s.sub_domain} value={s.sub_domain}>{s.sub_domain}</option>
                ))}
            </select>
          </div>
        </div>
  
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center w-100"
            style={{ minHeight: '300px' }}
          >
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              {/* Currency Summary Cards */}
                <div className="d-flex flex-wrap gap-2 mb-3">
                <div className="px-3 py-2 rounded" style={{ background: '#e8f5e9', minWidth: '140px' }}>
                  <p className="text-muted mb-0" style={{ fontSize: '11px' }}>Total (USD)</p>
                  <span className="fw-bold text-success" style={{ fontSize: '15px' }}>
                    $ {currencyTotals.USD.toLocaleString('en-US')}
                  </span>
                </div>
                <div className="px-3 py-2 rounded" style={{ background: '#e3f2fd', minWidth: '140px' }}>
                  <p className="text-muted mb-0" style={{ fontSize: '11px' }}>Total (EUR)</p>
                  <span className="fw-bold text-primary" style={{ fontSize: '15px' }}>
                    € {currencyTotals.EUR.toLocaleString('en-US')}
                  </span>
                </div>
                <div className="px-3 py-2 rounded" style={{ background: '#fff8e1', minWidth: '140px' }}>
                  <p className="text-muted mb-0" style={{ fontSize: '11px' }}>Total (MYR)</p>
                  <span className="fw-bold text-warning" style={{ fontSize: '15px' }}>
                    RM {currencyTotals.MYR.toLocaleString('en-US')}
                  </span>
                </div>
              </div>

              <div className="row">
                <div className="col-8">
                  <p className="fs-4 text-dark">
                    Conference Management / Participant Form / Approved Onsite Participant Form
                  </p>
                </div>
              </div>
              
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                responsive
                striped
                paginationPerPage={50}
                paginationComponentOptions={{ noRowsPerPage: true }}
                onChangePage={handlePageChange}
                subHeader
                subHeaderComponent={
                  <div>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search"
                      value={searchText}
                      onChange={handleSearchChange}
                    />
                  </div>
                }
              />

              {/* View Only Modal */}
              <Modal className="modal-lg" backdrop="static" show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>View Onsite Participant Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Table striped bordered hover responsive className="table-sm">
                    <thead>
                      <tr>
                        <th className="bg-secondary text-white" colSpan={2}>Payment Receipt Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>Payment ID</td><td>{form.payment_id}</td></tr>
                      <tr><td>Participant Type</td><td>{form.participant_type}</td></tr>
                    </tbody>
                    <br />
                    <thead>
                      <tr>
                        <th className="bg-secondary text-white" colSpan={2}>Billing Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>Full Name</td><td>{form.billing_full_name}</td></tr>
                      <tr><td>Email</td><td>{form.billing_email}</td></tr>
                      <tr><td>Phone No</td><td>{form.billing_phone_no}</td></tr>
                      <tr><td>Address</td><td>{form.billing_address}</td></tr>
                      <tr><td>Country</td><td>{getCountryName(form.billing_countries)}</td></tr>
                    </tbody>
                    <br />                   
                    <thead>
                      <tr>
                        <th className="bg-secondary text-white" colSpan={2}>Payment Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>Mode of Payment</td><td>{getPaymentTypeName(form.payment_type)}</td></tr>
                      <tr><td>Currency</td><td>{form.currency_type}</td></tr>
                      <tr><td>Registration Amount</td><td>{form.reg_amount}</td></tr>
                      <tr><td>Registration Type</td><td>{form.reg_type}</td></tr>
                      <tr><td>Registration Category</td><td>{form.reg_category}</td></tr>
                      <tr><td>No. of Ticket</td><td>{form.no_of_ticket}</td></tr>
                      {form.occupancy_type && (
                        <tr><td>Occupancy</td><td>{form.occupancy_type}</td></tr>
                      )}
                      {form.total_no_of_nights && (
                        <tr><td>No. of Nights</td><td>{form.total_no_of_nights}</td></tr>
                      )}
                    </tbody>
                  </Table>
                </Modal.Body>
                <Modal.Footer>
                <Button variant="danger" onClick={handleClose}>
                    <i className="align-middle far fa-fw fa-times-circle"></i> Close Payment
                </Button>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                    Cancel
                </Button>
                </Modal.Footer>
              </Modal>

            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovedOnsiteParticipantForm;